import React, { useRef, useState } from 'react'
import { Col, Form, Icon, Row, Typography, notification } from 'antd'
import { Formik } from 'formik'
import { Location } from 'history'
import { Button, Card, PageHeader, Table } from 'src/components/Ant'
import { RouteComponentProps } from 'react-router-dom'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Input } from 'src/components/Formik'
import { Organization, Tenant } from 'src/types'
import * as TenantService from 'src/services/api/tenant'
import {
  DrawerOrganizationForm,
  emptyDrawerValues,
  initialDrawerValues,
  initialTenantValues,
  PartialTenant
} from './initial-values'
import { validationTenantSchema } from './validation-schema'
import * as CSS from './styles'
import sanitizeToPost from './sanitize-post'
import TenantDrawer from './drawer'

type Params = {
  tenant_id?: string
}

let initialValues = {} as PartialTenant

export default function ManageTenantForm({
  history
}: RouteComponentProps<Params>): JSX.Element {
  const [isLoading, setIsLoading] = useState<boolean>()
  const refFormik = useRef<Formik<PartialTenant>>(null)
  const refFormikDrawer = useRef<Formik<DrawerOrganizationForm>>(null)
  const { t } = useTranslation()

  const teste: Location<{ record: Tenant }> = useLocation()

  if (teste?.state?.record) {
    const { organizations, tenant_id, tenant_identity, tenant_name } =
      teste.state.record

    initialValues = {
      organizations,
      tenant_id,
      tenant_organization_name: tenant_identity ?? tenant_name ?? '',
      tenant_sufix: ''
    } as PartialTenant
  } else {
    initialValues = initialTenantValues
  }

  function handleSubmit(values: PartialTenant) {
    setIsLoading(true)
    const sanitized = sanitizeToPost(values)

    TenantService.save(sanitized)
      .then(response => {
        if (response.status === 200) {
          const description = `tenant_id: ${
            response.data?.tenant_id ?? '- '
          } \n organization.id ${response.data?.organizations
            ?.map(id => id?.organization_id ?? '-')
            .join(',')}`

          notification.success({
            duration: 10,
            message: (
              <Typography.Text
                copyable={{ text: `${response.data?.tenant_id ?? ''}` }}
              >
                {t('web.tenant.saved-successfully')}
              </Typography.Text>
            ),
            description,
            placement: 'bottomRight'
          })
          history.goBack()
          return
        }
        notification.error({
          message: JSON.stringify(response.data),
          duration: 8
        })
      })
      .catch(response => {
        notification.error({
          message: JSON.stringify(response.data),
          duration: 8
        })
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  function resetDrawerForm(values: Organization): void {
    const {
      address,
      code,
      contacts,
      legal_name,
      organization_id,
      organization_type,
      trade_name,
      user_limit,
      vat_number
    } = values

    const firstEmailContact =
      contacts.filter(c => c.contact_type === 'EMAIL')?.[0]?.email_contacts[0]
        .email ?? ''
    const firstPhoneContact =
      contacts.filter(c => c.contact_type === 'PHONE')?.[0]?.phone_contacts[0]
        .phone_number ?? ''

    const formValues = {
      address_number: address.address_number,
      address: address.address,
      city_id: address.city_id,
      city: address.city,
      code,
      country_id: address.country_id,
      country: address.country,
      description: address.description,
      email: firstPhoneContact,
      legal_name,
      organization_id,
      organization_type,
      phone_contact: firstEmailContact,
      state_id: address.state_id,
      state: address.state,
      trade_name,
      user_limit,
      vat_number
    }
    refFormikDrawer.current.resetForm(formValues)
  }

  function handleSubmitDrawer(values: DrawerOrganizationForm): void {
    refFormikDrawer.current.resetForm(emptyDrawerValues)
    const oldOrganizationsList =
      refFormik.current.getFormikBag().values.organizations

    const newOrganization: Organization = {
      active: true,
      address: {
        address_id: null,
        address_number: values.address_number,
        address: values.address,
        city_id: values.city_id,
        city: values.city,
        country_id: values.country_id,
        country: values.country,
        description: values.description,
        state_id: values.state_id,
        state: values.state,
        zip_code: null
      },
      code: values.code,
      contacts: [],
      contract: null,
      created_by: null,
      created_on: null,
      current_users: 1,
      legal_name: values.legal_name,
      organization_id: Math.random().toString(),
      organization_type: values.organization_type,
      preferred_language: 'PT',
      tenant: null,
      trade_name: values.trade_name,
      updated_by: null,
      updated_on: null,
      user_limit: values.user_limit,
      vat_number: values.vat_number
    }

    if (values.email) {
      newOrganization.contacts.push({
        contact_id: null,
        contact_type: 'EMAIL',
        description: '',
        user_id: null,
        email_contacts: [
          {
            contact_id: '',
            email: values.email,
            main_email: true,
            verified: false
          }
        ],
        organization_id: '',
        phone_contacts: []
      })
    }

    if (values.phone_contact) {
      newOrganization.contacts.push({
        contact_id: null,
        contact_type: 'PHONE',
        description: '',
        user_id: null,
        email_contacts: [],
        organization_id: '',
        phone_contacts: [
          {
            calling_code: '',
            contact_id: '',
            is_whatsapp: false,
            main_phone: false,
            phone_number: values.phone_contact
          }
        ]
      })
    }

    refFormik.current
      .getFormikActions()
      .setFieldValue('organizations', [
        ...oldOrganizationsList,
        newOrganization
      ])
  }

  return (
    <>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        ref={refFormik}
        render={formikProps => (
          <Form onSubmit={formikProps.handleSubmit}>
            <PageHeader
              extra={[
                <Button
                  key="common.actions.cancel"
                  loading={isLoading}
                  onClick={() => history.goBack()}
                  size="default"
                  title="common.actions.cancel"
                  type="default"
                />,
                <Button
                  htmlType="submit"
                  key="common.actions.save"
                  loading={isLoading}
                  size="default"
                  title="common.actions.save"
                  type="primary"
                />
              ]}
              title="web.tenant.title"
            />
            <CSS.Content>
              <Card>
                <Row gutter={24}>
                  <Col span={11}>
                    <Input
                      autoFocus
                      disabled={
                        typeof formikProps.values.tenant_id !== 'string'
                      }
                      formikProps={formikProps}
                      label="web.tenant.organization-name"
                      name="tenant_organization_name"
                    />
                  </Col>
                  <Col offset={2} span={11}>
                    <Input
                      disabled={
                        typeof formikProps.values.tenant_id !== 'string'
                      }
                      formikProps={formikProps}
                      label="web.tenant.sufix"
                      name="tenant_sufix"
                    />
                  </Col>
                </Row>
                <Row gutter={24}>
                  <Col span={24}>
                    <Button
                      htmlType="button"
                      key="common.actions.save"
                      size="default"
                      title="web.organization.new-organization"
                      type="primary"
                      onClick={() => {
                        refFormikDrawer.current.resetForm(initialDrawerValues)
                      }}
                    />
                  </Col>
                </Row>
                <Row gutter={24}>
                  <Col span={24}>
                    <Table
                      className="table-striped"
                      columns={[
                        {
                          dataIndex: 'code',
                          title: 'web.resources.common.code.title'
                        },
                        {
                          dataIndex: 'trade_name',
                          title: 'web.organization.trade-name'
                        },
                        {
                          dataIndex: 'user_limit',
                          title: 'web.user.title'
                        },
                        {
                          dataIndex: 'active',
                          title: 'web.resources.common.active',
                          render: active => active && <Icon type="check" />
                        },
                        {
                          align: 'center',
                          dataIndex: '',
                          render: record => (
                            <div
                              onClick={e => {
                                e.preventDefault()
                                e.stopPropagation()
                                formikProps.setFieldValue(
                                  'organizations',
                                  formikProps.values.organizations.filter(
                                    org =>
                                      org.organization_id !==
                                      record.organization_id
                                  )
                                )
                              }}
                            >
                              {typeof record.organization_id === 'string' && (
                                <Icon
                                  theme="twoTone"
                                  twoToneColor="red"
                                  type="stop"
                                />
                              )}
                            </div>
                          ),
                          title: 'common.actions.remove',
                          width: 100
                        }
                      ]}
                      dataSource={formikProps.values.organizations}
                      loading={isLoading}
                      onRow={record => ({
                        onClick: () => {
                          if (typeof record.organization_id === 'string') {
                            resetDrawerForm(record)
                          }
                        }
                      })}
                    />
                  </Col>
                </Row>
              </Card>
            </CSS.Content>
          </Form>
        )}
        validationSchema={validationTenantSchema}
      />
      <TenantDrawer refFormik={refFormikDrawer} onSubmit={handleSubmitDrawer} />
    </>
  )
}
